import { useState, useEffect, useMemo } from 'react';
import api, { customersApi } from '../../../services/api';
import { Customer, DealListItem, PaginatedResponse } from '../../../types';

export const useDeals = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | ''>('');
  const [stage, setStage] = useState<string>('');
  const [data, setData] = useState<PaginatedResponse<DealListItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const load = async (page?: number) => {
    try {
      setLoading(true);
      const res = await api.get<PaginatedResponse<DealListItem>>('/deals', {
        params: {
          customer_id: selectedCustomer === '' ? undefined : Number(selectedCustomer),
          stage: stage || undefined,
          page,
        },
      });
      setData(res.data);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      // Fetch all customers (not just active) so deals referencing inactive customers still show names
      const cs = await customersApi.getAll({ per_page: 1000 });
      setCustomers(cs.data);
    })();
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, stage]);

  // Filter deals based on search query
  const filteredDeals = useMemo(() => {
    if (!data?.data) return [];
    if (!debouncedSearchQuery.trim()) return data.data;

    const query = debouncedSearchQuery.toLowerCase();
    return data.data.filter(
      (d) =>
        d.name.toLowerCase().includes(query) ||
        (customers.find((c) => c.id === d.customer_id)?.item || '').toLowerCase().includes(query) ||
        d.stage.toLowerCase().includes(query) ||
        (d.project?.project_number || '').toLowerCase().includes(query),
    );
  }, [data?.data, debouncedSearchQuery, customers]);

  return {
    customers,
    selectedCustomer,
    setSelectedCustomer,
    stage,
    setStage,
    data,
    loading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    filteredDeals,
    load,
  };
};
